import { api } from './apiClient';

const BASE = '/api/v1/admin/practice';

export const formatPracticeError = (error, fallback = 'Request failed') => {
  const data = error?.response?.data;
  if (!data) {
    if (error?.request) {
      return 'Unable to reach the server. Please check your internet connection or try again later.';
    }
    return error?.message || fallback;
  }
  if (Array.isArray(data.errors) && data.errors.length) {
    const head = data.message || 'Validation failed';
    return [head, ...data.errors].join('\n');
  }
  if (typeof data.errors === 'string' && data.errors) {
    return data.message && data.message !== data.errors
      ? `${data.message}\n${data.errors}`
      : data.errors;
  }
  return data.message || fallback;
};

const request = async (fn, fallback) => {
  try {
    const response = await fn();
    return response.data;
  } catch (error) {
    const err = new Error(formatPracticeError(error, fallback));
    err.payload = error?.response?.data;
    throw err;
  }
};

const withRootTotals = (body, pageSize) => {
  const totalCount = Number(body?.totalCount || 0);
  const size = Number(pageSize) || 16;
  return {
    ...body,
    total: totalCount,
    totalCount,
    totalPages: Math.max(1, Math.ceil(totalCount / size) || 1),
  };
};

const listBody = (page, pageSize, searchtext, extra = {}) => {
  const body = {
    pagination: {
      page,
      pageSize,
      sort: { order: 1, _id: 1 },
    },
    ...extra,
  };
  if (searchtext) body.searchtext = searchtext;
  return body;
};

export const listPracticeModules = async (page = 1, pageSize = 16, searchtext = '') => {
  const body = await request(
    () => api.post(`${BASE}/modules/list`, listBody(page, pageSize, searchtext)),
    'Failed to load practice modules'
  );
  return withRootTotals(body, pageSize);
};

export const createPracticeModule = async (moduleData) => {
  const payload = {
    title: moduleData.title,
    description: moduleData.description,
  };
  if (moduleData.thumbnail_url) payload.thumbnail_url = moduleData.thumbnail_url;
  if (moduleData.order !== undefined && moduleData.order !== null && moduleData.order !== '') {
    payload.order = Number(moduleData.order);
  }
  return request(() => api.post(`${BASE}/modules`, payload), 'Failed to create practice module');
};

export const updatePracticeModule = async (moduleData) => {
  const payload = { module_id: moduleData.module_id };
  if (moduleData.title !== undefined) payload.title = moduleData.title;
  if (moduleData.description !== undefined) payload.description = moduleData.description;
  if (moduleData.thumbnail_url !== undefined) payload.thumbnail_url = moduleData.thumbnail_url || null;
  if (moduleData.order !== undefined) payload.order = Number(moduleData.order);
  if (typeof moduleData.is_active === 'boolean') payload.is_active = moduleData.is_active;
  return request(() => api.put(`${BASE}/modules`, payload), 'Failed to update practice module');
};

export const deletePracticeModule = async (moduleId) =>
  request(() => api.delete(`${BASE}/modules/${moduleId}`), 'Failed to delete practice module');

export const listPracticeUnits = async (moduleId, page = 1, pageSize = 16, searchtext = '') => {
  const extra = {};
  if (moduleId) extra.module_id = moduleId;
  const body = await request(
    () => api.post(`${BASE}/units/list`, listBody(page, pageSize, searchtext, extra)),
    'Failed to load practice units'
  );
  return withRootTotals(body, pageSize);
};

export const createPracticeUnit = async (unitData) => {
  const payload = {
    module_id: unitData.module_id,
    title: unitData.title,
  };
  if (unitData.description) payload.description = unitData.description;
  if (unitData.order !== undefined && unitData.order !== '') payload.order = Number(unitData.order);
  if (unitData.thumbnail_url) payload.thumbnail_url = unitData.thumbnail_url;
  if (unitData.content_type) payload.content_type = unitData.content_type;
  if (unitData.icon_type) payload.icon_type = unitData.icon_type;
  if (unitData.points !== undefined && unitData.points !== '') payload.points = Number(unitData.points);
  if (unitData.quiz_coins !== undefined && unitData.quiz_coins !== '') payload.quiz_coins = Number(unitData.quiz_coins);
  return request(() => api.post(`${BASE}/units`, payload), 'Failed to create practice unit');
};

export const updatePracticeUnit = async (unitData) => {
  const payload = { unit_id: unitData.unit_id };
  ['module_id', 'title', 'description', 'thumbnail_url', 'content_type', 'icon_type'].forEach((key) => {
    if (unitData[key] !== undefined) payload[key] = unitData[key];
  });
  if (unitData.order !== undefined) payload.order = Number(unitData.order);
  if (unitData.points !== undefined) payload.points = Number(unitData.points);
  if (unitData.quiz_coins !== undefined) payload.quiz_coins = Number(unitData.quiz_coins);
  if (typeof unitData.is_active === 'boolean') payload.is_active = unitData.is_active;
  return request(() => api.put(`${BASE}/units`, payload), 'Failed to update practice unit');
};

export const deletePracticeUnit = async (unitId) =>
  request(() => api.delete(`${BASE}/units/${unitId}`), 'Failed to delete practice unit');

export const listPracticeLessons = async ({ unitId, type, searchtext, page = 1, pageSize = 15 } = {}) => {
  const body = { page, pageSize };
  if (unitId) body.unit_id = unitId;
  if (type) body.type = type;
  if (searchtext) body.searchtext = searchtext;
  return request(() => api.post(`${BASE}/lessons/list`, body), 'Failed to load practice lessons');
};

export const createPracticeLesson = async (payload) =>
  request(() => api.post(`${BASE}/lessons`, payload), 'Failed to create practice lesson');

export const updatePracticeLesson = async (lessonId, payload) =>
  request(() => api.put(`${BASE}/lessons/${lessonId}`, payload), 'Failed to update practice lesson');

export const deletePracticeLesson = async (lessonId) =>
  request(() => api.delete(`${BASE}/lessons/${lessonId}`), 'Failed to delete practice lesson');

export const extractPracticeViewItems = async ({ viewItemId, lessonId } = {}) => {
  const path = viewItemId
    ? `${BASE}/lessons/view-items/extract-from-html/${viewItemId}`
    : `${BASE}/lessons/view-items/extract-from-html`;
  const body = {};
  if (!viewItemId && lessonId) body.lesson_id = lessonId;
  return request(() => api.post(path, body), 'Failed to extract view items');
};

const CHOICE_TYPES = ['text_text', 'audio_text', 'image_text', 'audio_audio'];

const buildQuizPayload = (quizData, { isUpdate } = {}) => {
  const payload = {};
  if (isUpdate) payload.question_id = quizData.question_id || quizData.id;
  if (quizData.content_id !== undefined) payload.content_id = quizData.content_id;
  if (quizData.question_type !== undefined) payload.question_type = quizData.question_type;
  if (quizData.question_text !== undefined) payload.question_text = quizData.question_text || '';
  if (quizData.explanation !== undefined) payload.explanation = quizData.explanation || '';
  if (quizData.question_audio_url !== undefined && quizData.question_audio_url) {
    payload.question_audio_url = quizData.question_audio_url;
  }
  if (quizData.question_image_url !== undefined && quizData.question_image_url) {
    payload.question_image_url = quizData.question_image_url;
  }
  if (quizData.points !== undefined && quizData.points !== '') payload.points = Number(quizData.points);
  if (quizData.coins !== undefined && quizData.coins !== '') payload.coins = Number(quizData.coins);
  if (quizData.order !== undefined && quizData.order !== '') payload.order = Number(quizData.order);
  if (['easy', 'medium', 'hard'].includes(quizData.difficulty_level)) {
    payload.difficulty_level = quizData.difficulty_level;
  }
  if (typeof quizData.is_active === 'boolean') payload.is_active = quizData.is_active;

  const type = quizData.question_type;
  if (CHOICE_TYPES.includes(type) && Array.isArray(quizData.options)) {
    let order = 1;
    const correctIndex = quizData.correct_answer_index !== undefined ? quizData.correct_answer_index : 0;
    payload.options = quizData.options
      .filter((opt) => (opt.text || opt.option_text || '').trim())
      .map((opt, index) => ({
        option_text: (opt.text || opt.option_text).trim(),
        is_correct: index === correctIndex,
        order: order++,
      }));
  }
  if (type === 'match_pairs' && Array.isArray(quizData.match_pairs)) {
    payload.match_pairs = quizData.match_pairs
      .filter((pair) => pair.left_item?.trim() && pair.right_item?.trim())
      .map((pair, index) => ({
        left_item: pair.left_item.trim(),
        right_item: pair.right_item.trim(),
        order: pair.order !== undefined ? pair.order : index + 1,
      }));
  }
  if (type === 'reorder_text' && quizData.reorder_correct_order !== undefined) {
    payload.reorder_correct_order = (quizData.reorder_correct_order || [])
      .map((item) => (typeof item === 'string' ? item : item.text || ''))
      .filter((text) => text.trim());
  }
  if (type === 'fill_blank_text' && quizData.fill_blank_correct_order !== undefined) {
    payload.fill_blank_correct_order = (quizData.fill_blank_correct_order || []).map((seg) => ({
      title: seg.title ?? '',
      isBlank: Boolean(seg.isBlank),
    }));
  }
  return payload;
};

export const listPracticeQuiz = async (page = 1, pageSize = 16, searchtext = '', contentId) => {
  const extra = {};
  if (contentId) extra.content_id = contentId;
  const body = await request(
    () => api.post(`${BASE}/quiz-questions/list`, listBody(page, pageSize, searchtext, extra)),
    'Failed to load practice quiz questions'
  );
  return withRootTotals(body, pageSize);
};

export const getPracticeQuiz = async (questionId) =>
  request(() => api.get(`${BASE}/quiz-questions/${questionId}`), 'Failed to load practice quiz question');

export const createPracticeQuiz = async (quizData) => {
  const payload = buildQuizPayload({
    ...quizData,
    points: quizData.points === '' || quizData.points === undefined ? 5 : quizData.points,
    coins: quizData.coins === '' || quizData.coins === undefined ? 0 : quizData.coins,
    order: quizData.order === '' || quizData.order === undefined ? 0 : quizData.order,
  });
  return request(() => api.post(`${BASE}/quiz-questions`, payload), 'Failed to create practice quiz question');
};

export const updatePracticeQuiz = async (quizData) => {
  const payload = buildQuizPayload(quizData, { isUpdate: true });
  return request(() => api.put(`${BASE}/quiz-questions`, payload), 'Failed to update practice quiz question');
};

export const deletePracticeQuiz = async (questionId) =>
  request(() => api.delete(`${BASE}/quiz-questions/${questionId}`), 'Failed to delete practice quiz question');

export const uploadPracticeJson = async (file, params = {}) => {
  if (!file) throw new Error('JSON file is required');
  const formData = new FormData();
  formData.append('json_file', file);
  if (params.module_id) formData.append('module_id', params.module_id);
  if (params.unit_id) formData.append('unit_id', params.unit_id);
  if (params.lesson_content_id) formData.append('lesson_content_id', params.lesson_content_id);
  try {
    const response = await api.post(`${BASE}/quiz-questions/upload-json`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000,
    });
    return response.data;
  } catch (error) {
    const err = new Error(formatPracticeError(error, 'Failed to upload JSON'));
    err.payload = error?.response?.data;
    throw err;
  }
};
